/**
 * Token Manager
 *
 * Manages access token retrieval for API requests.
 * This allows the API client to get tokens without React dependencies.
 */

let tokenGetter: (() => Promise<null | string>) | null = null;

/**
 * Set the token getter function
 * This should be called from the auth provider to provide access to Kinde's getAccessToken
 */
export const setTokenGetter = (getter: () => Promise<null | string>) => {
	tokenGetter = getter;
};

/**
 * Get the current access token
 * Returns null if no token is available or if token getter is not set
 */
export const getAccessToken = async (): Promise<null | string> => {
	if (!tokenGetter) {
		return null;
	}
	const getter = tokenGetter;
	// Axios's network timeout starts after request interceptors. Bound Kinde's
	// lookup too, so a stalled refresh cannot leave callers busy indefinitely.
	// Reject rather than sending a configuration body without authorization.
	return new Promise((resolve, reject) => {
		const timer = setTimeout(() => {
			reject(new Error("Authorization timed out. Sign in again and retry."));
		}, 10000);
		void Promise.resolve()
			.then(getter)
			.then(resolve, () => {
				reject(
					new Error("Authorization unavailable. Sign in again and retry."),
				);
			})
			.finally(() => clearTimeout(timer));
	});
};
